import { contentRevision } from "game-common/content/catalog"
import { Protocol } from "game-common/protocol"

/**
 * A live view of somebody else's session, for the /admin replay viewer: a real socket to the game
 * server that asks to WATCH a session (type_adminWatch) instead of logging in.
 *
 * The server answers with the watched player's entity id, a snapshot of their room built for this
 * viewer alone, and then a copy of everything it sends them -- the same stream a recording holds, so
 * the client renders it the same way. The client still believes it is playing, so it keeps talking:
 * its input loop, capability notes, a login attempt in answer to the server's greeting. Only the few
 * read-only requests a viewer can usefully make get through; the server ignores anything else from a
 * viewer anyway, but there is no reason to send it.
 */

const VIEWER_SENDS = new Set<string>([
    "pong",
    Protocol.type_requestEntityDetail,
    Protocol.type_requestRoomSnapshot,
    Protocol.type_resumeUpdates,
])

/** WebSocket-shaped: the fields and methods Client.connectImpl touches. */
export class WatchSocket {
    readyState = 0
    onopen?: () => void
    onmessage?: (event: { data: string }) => void
    onclose?: () => void
    onerror?: (error: any) => void
    /** Why the server stopped (type_watchEnded): "left", "notLive" or "unauthorized". */
    endedReason?: string
    /** When the last message arrived, to tell a quiet session from a dead socket. */
    lastMessageTs = 0

    private socket?: WebSocket

    constructor(private url: string, private token: string, private sessionId: string, private onEnded: (reason: string) => void) {}

    /** Dial, once the client has put its handlers on. */
    start = () => {
        const socket = new WebSocket(this.url)
        this.socket = socket
        socket.onopen = () => {
            this.readyState = 1
            socket.send(JSON.stringify({ contentRevision, payloadType: Protocol.type_adminWatch, payload: { token: this.token, sessionId: this.sessionId } }))
            this.onopen?.()
        }
        socket.onmessage = event => {
            this.lastMessageTs = Date.now()
            const text = String(event.data)
            if (text.startsWith(`{"payloadType":"${Protocol.type_watchEnded}"`)) {
                try {
                    this.endedReason = JSON.parse(text).payload?.reason || "ended"
                } catch {
                    this.endedReason = "ended"
                }
                this.onEnded(this.endedReason)
                return
            }
            this.onmessage?.({ data: text })
        }
        socket.onerror = error => this.onerror?.(error)
        socket.onclose = () => {
            this.readyState = 3
            if (!this.endedReason) {
                this.endedReason = "disconnected"
                this.onEnded(this.endedReason)
            }
            this.onclose?.()
        }
    }

    send = (text: string) => {
        if (this.readyState !== 1 || !this.socket) return
        let payloadType: string | undefined
        try {
            payloadType = JSON.parse(text)?.payloadType
        } catch {
            return
        }
        if (payloadType && VIEWER_SENDS.has(payloadType)) this.socket.send(text)
    }

    close = () => {
        this.socket?.close()
    }
}
